<template>
  <div id="google-one-tap-container" style="display: none"></div>
</template>

<script lang="ts" setup>
import { DebouncedFunc, throttle } from 'lodash-es';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import { trackGoogleOneTapImpression } from '@/components/GoogleOneTap/GoogleOneTap.tracking';
import { isMobileViewport } from '@/helpers/component-helpers';
import { logError } from '@/helpers/LogHelper';
import { GoogleCredentialResponse, handleCredentialResponse } from './googleOneTapHelper';

defineOptions({
  name: 'GoogleOneTap',
});

const scriptLoaded = ref(false);
const isOneTapActive = ref(false);
const resizeHandler = ref(undefined as DebouncedFunc<() => void> | undefined);

const clientId = computed((): string => {
  return import.meta.env.VUE_GOOGLE_ONE_TAP_CLIENT_ID || '';
});

const initGoogleOneTap = (): void => {
  if (!window.google || !clientId.value || !scriptLoaded.value) return;

  // Check viewport eligibility before initializing
  if (isMobileViewport()) {
    return;
  }

  try {
    window.google.accounts.id.initialize({
      client_id: clientId.value,
      callback: (res: GoogleCredentialResponse) => handleCredentialResponse(res),
      cancel_on_tap_outside: false,
      itp_support: true,
      use_fedcm_for_prompt: true,
    });

    window.google.accounts.id.prompt();
    isOneTapActive.value = true;
    trackGoogleOneTapImpression();
  } catch (error) {
    logError('Error initializing Google One Tap', undefined, error);
  }
};

const cancelGoogleOneTap = (): void => {
  try {
    if (typeof window !== 'undefined' && window.google?.accounts?.id) {
      window.google.accounts.id.cancel();
      isOneTapActive.value = false;
    }
  } catch (error) {
    logError('Error canceling Google One Tap', undefined, error);
  }
};

const loadGoogleScript = () => {
  if (scriptLoaded.value) return;

  const script = document.createElement('script');
  script.src = 'https://accounts.google.com/gsi/client';
  script.async = true;
  script.defer = true;

  script.onload = () => {
    scriptLoaded.value = true;
    // Script loaded - attempt initialization (viewport check happens in initGoogleOneTap)
    initGoogleOneTap();
  };

  script.onerror = (_error) => {
    logError('Failed to load Google One Tap script');
  };

  document.head.appendChild(script);
};

onMounted(() => {
  loadGoogleScript();

  // Monitor viewport changes - handle mobile ↔ desktop transitions
  resizeHandler.value = throttle(() => {
    const isEligible = !isMobileViewport();

    if (!isEligible && isOneTapActive.value) {
      // Cancel if viewport becomes mobile
      cancelGoogleOneTap();
    } else if (isEligible && !isOneTapActive.value && scriptLoaded.value) {
      // Initialize if viewport becomes desktop and not already active
      initGoogleOneTap();
    }
  }, 500);

  if (typeof window !== 'undefined') {
    window.addEventListener('resize', resizeHandler.value);
  }
});

onBeforeUnmount(() => {
  cancelGoogleOneTap();

  if (typeof window !== 'undefined' && resizeHandler.value) {
    window.removeEventListener('resize', resizeHandler.value);
  }
});
</script>
